import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input, Modal, ModalHeader, Spinner } from "reactstrap";
import { FormikErrors, useFormik } from "formik";
import { toast } from "react-toastify";

import CountrySelect from "Components/Common/CountrySelect";

/**
 * One form for short lists kept inside a profile: phone numbers, email
 * addresses, addresses, alias names, plus single fields such as a website.
 *
 * The lists are small and edited together, so they save as one. With more
 * than one section, an index on the left jumps between them. Older entries
 * often hold spreadsheet damage ("2.52616E+11", "N/A"), so problems in
 * untouched entries are warnings to fix or remove, and only new or edited
 * entries block a save.
 */

export type SheetSection =
  | {
      kind: "list";
      id: string;
      label: string;
      icon: string;
      noun: string;
      plural: string;
      placeholder: string;
      inputType?: "text" | "tel" | "email";
      hint?: string;
      /** Format check for each entry. */
      check?: "email" | "phone";
    }
  | {
      kind: "address";
      id: string;
      label: string;
      icon: string;
      noun: string;
      plural: string;
      hint?: string;
    }
  | {
      kind: "single";
      id: string;
      label: string;
      icon: string;
      placeholder: string;
      hint?: string;
      check?: "web";
    };

type TextRow = { key: string; id?: number; value: string };
type AddressRow = { key: string; id?: number; address: string; city: string; country: string | null };
type Values = Record<string, TextRow[] | AddressRow[] | string>;
type Issue = { level: "error" | "warning"; message: string };

let rowSeed = 0;
const newKey = () => `row-${++rowSeed}`;

const toTextRows = (items: any[] | undefined): TextRow[] =>
  (items || []).map((item) => ({ key: newKey(), id: item?.id, value: item?.name || "" }));

const toAddressRows = (items: any[] | undefined): AddressRow[] =>
  (items || []).map((item) => ({
    key: newKey(),
    id: item?.id,
    address: item?.address || "",
    city: item?.city || "",
    country: item?.country || null,
  }));

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const SPREADSHEET_NUMBER = /^-?\d+(\.\d+)?e[+-]?\d+$/i;
const WEB_ADDRESS = /^(https?:\/\/)?[^\s/]+\.[^\s]{2,}$/i;

/** Problems with one list entry. `changed` decides whether they block a save. */
const listIssue = (
  check: "email" | "phone" | undefined,
  value: string,
  earlier: string[],
  changed: boolean
): Issue | null => {
  const entry = value.trim();
  if (!entry) return null;
  const level = changed ? "error" : "warning";
  if (earlier.some((other) => other.trim().toLowerCase() === entry.toLowerCase())) {
    return { level, message: "Already listed. Remove one of the two." };
  }
  if (check === "email" && !EMAIL.test(entry)) {
    return {
      level,
      message: changed
        ? "Enter a full email address, like name@example.org."
        : `“${entry}” is not an email address. Fix it or remove it.`,
    };
  }
  if (check === "phone") {
    if (SPREADSHEET_NUMBER.test(entry)) {
      return { level, message: "This number was damaged by a spreadsheet. Enter it in full again." };
    }
    if ((entry.match(/\d/g) || []).length < 5) {
      return { level, message: "This does not look like a phone number." };
    }
  }
  return null;
};

const singleIssue = (check: "web" | undefined, value: string, changed: boolean): Issue | null => {
  const entry = value.trim();
  if (!entry || check !== "web" || WEB_ADDRESS.test(entry)) return null;
  return {
    level: changed ? "error" : "warning",
    message: changed
      ? "Enter a web address, like www.example.org."
      : `“${entry}” is not a web address. Fix it or clear it.`,
  };
};

const IssueText = ({ id, issue }: { id: string; issue: Issue | null }) =>
  issue ? (
    <div
      id={id}
      className={`contact-sheet__issue ${issue.level === "error" ? "is-error" : "is-warning"}`}
    >
      <i
        className={issue.level === "error" ? "ri-error-warning-line" : "ri-alert-line"}
        aria-hidden="true"
      ></i>
      {issue.message}
    </div>
  ) : null;

const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const ProfileSheetEditor = ({
  isOpen,
  toggle,
  title,
  owner,
  sections,
  save,
  savedMessage,
  refresh,
}: {
  isOpen: boolean;
  toggle: () => void;
  title: string;
  /** The profile record holding the lists, keyed by each section's id. */
  owner: any;
  sections: SheetSection[];
  /** Saves the cleaned values, keyed by section id. */
  save: (payload: Record<string, any>) => Promise<any>;
  savedMessage: string;
  refresh: () => Promise<any>;
}) => {
  const withIndex = sections.length > 1;

  const initialValues: Values = useMemo(() => {
    const values: Values = {};
    sections.forEach((section) => {
      if (section.kind === "list") values[section.id] = toTextRows(owner?.[section.id]);
      else if (section.kind === "address") values[section.id] = toAddressRows(owner?.[section.id]);
      else values[section.id] = owner?.[section.id] || "";
    });
    return values;
    // Rebuilt when the modal opens or the profile reloads after a save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, isOpen, sections]);

  /** What each saved entry held, so edits can be told apart from old data. */
  const savedText = useMemo(() => {
    const byKey = new Map<string, string>();
    sections.forEach((section) => {
      if (section.kind !== "list") return;
      (initialValues[section.id] as TextRow[]).forEach((row) => byKey.set(row.key, row.value));
    });
    return byKey;
  }, [initialValues, sections]);

  const issuesFor = useCallback(
    (values: Values) => {
      const issues: Record<string, Issue | null> = {};
      sections.forEach((section) => {
        if (section.kind === "list") {
          const rows = values[section.id] as TextRow[];
          rows.forEach((row, index) => {
            const changed = !savedText.has(row.key) || savedText.get(row.key) !== row.value;
            const earlier = rows.slice(0, index).map((other) => other.value);
            issues[row.key] = listIssue(section.check, row.value, earlier, changed);
          });
        } else if (section.kind === "single") {
          const value = values[section.id] as string;
          issues[section.id] = singleIssue(
            section.check,
            value,
            value !== initialValues[section.id]
          );
        }
      });
      return issues;
    },
    [initialValues, savedText, sections]
  );

  const [saveError, setSaveError] = useState("");
  const [guarding, setGuarding] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // The modal renders its content after opening, so the scroll container is
  // tracked as state to start the section observer once it exists.
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const attachBody = useCallback((node: HTMLDivElement | null) => {
    bodyRef.current = node;
    setBodyEl(node);
  }, []);
  const [activeSection, setActiveSection] = useState(sections[0]?.id);

  const form = useFormik<Values>({
    initialValues,
    enableReinitialize: true,
    validate: (values) =>
      (Object.values(issuesFor(values)).some((issue) => issue?.level === "error")
        ? { blocked: "blocked" }
        : {}) as FormikErrors<Values>,
    onSubmit: async (values) => {
      setSaveError("");
      const payload: Record<string, any> = {};
      sections.forEach((section) => {
        if (section.kind === "list") {
          payload[section.id] = (values[section.id] as TextRow[])
            .map((row) => ({ ...(row.id ? { id: row.id } : {}), name: row.value.trim() }))
            .filter((row) => row.name);
        } else if (section.kind === "address") {
          payload[section.id] = (values[section.id] as AddressRow[])
            .map((row) => ({
              ...(row.id ? { id: row.id } : {}),
              address: row.address.trim() || null,
              city: row.city.trim() || null,
              country: row.country || null,
            }))
            .filter((row) => row.address || row.city || row.country);
        } else {
          payload[section.id] = (values[section.id] as string).trim() || null;
        }
      });

      try {
        await save(payload);
        toast.success(savedMessage, { autoClose: 2000 });
        await refresh();
        toggle();
      } catch (error: any) {
        setSaveError(error?.response?.data?.error?.message || "The changes could not be saved.");
      }
    },
  });

  useEffect(() => {
    if (isOpen) {
      setGuarding(false);
      setSaveError("");
    }
  }, [isOpen]);

  const issues = useMemo(() => issuesFor(form.values), [form.values, issuesFor]);
  const issueKeys = Object.keys(issues).filter((key) => issues[key]);
  const blockingCount = issueKeys.filter((key) => issues[key]?.level === "error").length;
  const warningCount = issueKeys.length - blockingCount;

  // Highlight the section in view as the form scrolls.
  useEffect(() => {
    if (!bodyEl || !withIndex) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveSection(visible.target.getAttribute("data-section") || "");
      },
      { root: bodyEl, rootMargin: "0px 0px -65% 0px" }
    );
    bodyEl.querySelectorAll("[data-section]").forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [bodyEl, withIndex]);

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const goTo = (id: string) => {
    const target = bodyRef.current?.querySelector<HTMLElement>(`[data-section="${id}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
    target.querySelector<HTMLElement>("h6")?.focus({ preventScroll: true });
    setActiveSection(id);
  };

  const goToFirstIssue = () => {
    const body = bodyRef.current;
    const first =
      body?.querySelector<HTMLElement>(".contact-sheet__issue.is-error") ||
      body?.querySelector<HTMLElement>(".contact-sheet__issue");
    const field = first?.closest(".contact-sheet__row, .contact-sheet__address, .contact-sheet__field");
    field?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "center" });
    field?.querySelector<HTMLElement>("input, textarea")?.focus({ preventScroll: true });
  };

  const requestClose = () => {
    if (form.dirty && !form.isSubmitting) setGuarding(true);
    else toggle();
  };

  const focusLater = (selector: string) =>
    window.setTimeout(() => bodyRef.current?.querySelector<HTMLElement>(selector)?.focus(), 0);

  const rowsOf = <T,>(id: string) => form.values[id] as unknown as T[];

  const addRow = (section: SheetSection) => {
    if (section.kind === "list") {
      const row: TextRow = { key: newKey(), value: "" };
      form.setFieldValue(section.id, [...rowsOf<TextRow>(section.id), row]);
      focusLater(`[data-row="${row.key}"] input`);
    } else if (section.kind === "address") {
      const row: AddressRow = { key: newKey(), address: "", city: "", country: null };
      form.setFieldValue(section.id, [...rowsOf<AddressRow>(section.id), row]);
      focusLater(`[data-row="${row.key}"] textarea`);
    }
  };

  const removeRow = (id: string, key: string) =>
    form.setFieldValue(
      id,
      rowsOf<{ key: string }>(id).filter((row) => row.key !== key)
    );

  const countOf = (section: SheetSection) =>
    section.kind === "single"
      ? (form.values[section.id] as string).trim()
        ? 1
        : 0
      : rowsOf(section.id).length;

  const renderList = (section: Extract<SheetSection, { kind: "list" }>) => {
    const rows = rowsOf<TextRow>(section.id);
    return (
      <>
        {rows.length ? (
          <div className="contact-sheet__rows">
            {rows.map((row, index) => {
              const issue = issues[row.key];
              const inputId = `sheet-${section.id}-${row.key}`;
              return (
                <div className="contact-sheet__row" key={row.key} data-row={row.key}>
                  <div className="contact-sheet__control">
                    <Input
                      id={inputId}
                      type={section.inputType || "text"}
                      aria-label={`${section.noun} ${index + 1}`}
                      placeholder={section.placeholder}
                      value={row.value}
                      invalid={issue?.level === "error"}
                      className={issue?.level === "warning" ? "is-warning" : ""}
                      aria-describedby={issue ? `${inputId}-issue` : undefined}
                      onChange={(e) =>
                        form.setFieldValue(`${section.id}.${index}.value`, e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-ghost-secondary btn-icon contact-sheet__remove"
                      aria-label={`Remove ${section.noun} ${index + 1}`}
                      title={`Remove ${section.noun}`}
                      onClick={() => removeRow(section.id, row.key)}
                    >
                      <i className="ri-close-line" aria-hidden="true"></i>
                    </button>
                  </div>
                  <IssueText id={`${inputId}-issue`} issue={issue} />
                </div>
              );
            })}
          </div>
        ) : (
          <p className="contact-sheet__empty">No {section.plural} recorded.</p>
        )}
        <button type="button" className="btn btn-sm btn-soft-primary" onClick={() => addRow(section)}>
          <i className="ri-add-line me-1" aria-hidden="true"></i>
          Add {section.noun}
        </button>
      </>
    );
  };

  const renderAddresses = (section: Extract<SheetSection, { kind: "address" }>) => {
    const rows = rowsOf<AddressRow>(section.id);
    return (
      <>
        {rows.length ? (
          <div className="contact-sheet__rows">
            {rows.map((row, index) => {
              const base = `sheet-${section.id}-${row.key}`;
              const set = (field: string, value: any) =>
                form.setFieldValue(`${section.id}.${index}.${field}`, value);
              return (
                <div
                  className="contact-sheet__address"
                  key={row.key}
                  data-row={row.key}
                  role="group"
                  aria-labelledby={`${base}-heading`}
                >
                  <div className="contact-sheet__address-head">
                    <span id={`${base}-heading`} className="contact-sheet__address-legend">
                      {rows.length > 1
                        ? `${capitalise(section.noun)} ${index + 1}`
                        : capitalise(section.noun)}
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost-secondary btn-icon contact-sheet__remove"
                      aria-label={`Remove ${section.noun} ${index + 1}`}
                      title={`Remove ${section.noun}`}
                      onClick={() => removeRow(section.id, row.key)}
                    >
                      <i className="ri-close-line" aria-hidden="true"></i>
                    </button>
                  </div>
                  <div className="contact-sheet__address-grid">
                    <div className="contact-sheet__address-line">
                      <label htmlFor={`${base}-address`} className="form-label">
                        Address
                      </label>
                      <Input
                        id={`${base}-address`}
                        type="textarea"
                        rows={2}
                        placeholder="Street, building, district"
                        value={row.address}
                        onChange={(e) => set("address", e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor={`${base}-city`} className="form-label">
                        City
                      </label>
                      <Input
                        id={`${base}-city`}
                        placeholder="For example Mogadishu"
                        value={row.city}
                        onChange={(e) => set("city", e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor={`${base}-country`} className="form-label">
                        Country
                      </label>
                      <CountrySelect
                        inputId={`${base}-country`}
                        value={row.country}
                        onChange={(next) => set("country", next)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="contact-sheet__empty">No {section.plural} recorded.</p>
        )}
        <button type="button" className="btn btn-sm btn-soft-primary" onClick={() => addRow(section)}>
          <i className="ri-add-line me-1" aria-hidden="true"></i>
          Add {section.noun}
        </button>
      </>
    );
  };

  const renderSingle = (section: Extract<SheetSection, { kind: "single" }>) => {
    const issue = issues[section.id];
    const inputId = `sheet-${section.id}`;
    return (
      <div className="contact-sheet__field">
        <Input
          id={inputId}
          aria-label={section.label}
          placeholder={section.placeholder}
          value={form.values[section.id] as string}
          invalid={issue?.level === "error"}
          className={issue?.level === "warning" ? "is-warning" : ""}
          aria-describedby={issue ? `${inputId}-issue` : undefined}
          onChange={(e) => form.setFieldValue(section.id, e.target.value)}
        />
        <IssueText id={`${inputId}-issue`} issue={issue} />
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={requestClose}
      size={withIndex ? "xl" : "lg"}
      centered
      className="record-manager-modal"
      labelledBy="profile-sheet-title"
    >
      <ModalHeader className="p-3 border-bottom" toggle={requestClose} tag="div">
        <h5 id="profile-sheet-title" className="modal-title mb-0">
          {title}
        </h5>
      </ModalHeader>

      <form
        className={`record-manager contact-sheet${withIndex ? "" : " is-single"}`}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (blockingCount) {
            goToFirstIssue();
            return;
          }
          form.handleSubmit();
        }}
      >
        {withIndex ? (
          <nav className="record-manager__list-pane contact-sheet__index" aria-label={`${title} sections`}>
            <ul className="record-manager__list">
              {sections.map((section) => (
                <li key={section.id}>
                  <button
                    type="button"
                    className={`record-manager__item contact-sheet__index-item${
                      activeSection === section.id ? " is-selected" : ""
                    }`}
                    aria-current={activeSection === section.id ? "true" : undefined}
                    onClick={() => goTo(section.id)}
                  >
                    <span className="record-manager__avatar is-primary" aria-hidden="true">
                      <i className={section.icon}></i>
                    </span>
                    <span className="record-manager__item-title">{section.label}</span>
                    <span className="contact-sheet__count">{countOf(section)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}

        <div className="record-manager__detail-pane">
          {guarding ? (
            <div className="record-manager__notice is-warning record-manager__guard" role="alert">
              <i className="ri-error-warning-line" aria-hidden="true"></i>
              <span className="me-auto">You have unsaved changes.</span>
              <button type="button" className="btn btn-sm btn-light" onClick={() => setGuarding(false)}>
                Keep editing
              </button>
              <button
                type="button"
                className="btn btn-sm btn-warning"
                onClick={() => {
                  setGuarding(false);
                  form.resetForm();
                  toggle();
                }}
              >
                Discard and close
              </button>
            </div>
          ) : null}

          <div className="record-manager__detail-body contact-sheet__body" ref={attachBody}>
            {saveError ? (
              <div className="record-manager__notice is-danger mb-3" role="alert">
                <i className="ri-error-warning-line" aria-hidden="true"></i>
                <span>{saveError}</span>
              </div>
            ) : null}
            {sections.map((section) => (
              <section
                key={section.id}
                className="contact-sheet__section"
                data-section={section.id}
                aria-labelledby={`sheet-title-${section.id}`}
              >
                <h6 id={`sheet-title-${section.id}`} className="contact-sheet__title" tabIndex={-1}>
                  {section.label}
                </h6>
                {section.hint ? <p className="contact-sheet__hint">{section.hint}</p> : null}
                {section.kind === "list"
                  ? renderList(section)
                  : section.kind === "address"
                  ? renderAddresses(section)
                  : renderSingle(section)}
              </section>
            ))}
          </div>

          <div className="record-manager__detail-foot">
            {issueKeys.length ? (
              <button
                type="button"
                className={`btn btn-link p-0 contact-sheet__issues-link ${
                  blockingCount ? "text-ink-danger" : "text-ink-warning"
                }`}
                onClick={goToFirstIssue}
              >
                <i className="ri-alert-line me-1" aria-hidden="true"></i>
                {blockingCount
                  ? `${blockingCount} ${blockingCount === 1 ? "entry needs" : "entries need"} fixing before saving`
                  : `${warningCount} older ${warningCount === 1 ? "entry looks" : "entries look"} wrong`}
              </button>
            ) : null}
            <div className="ms-auto d-flex gap-2">
              {form.dirty ? (
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    form.resetForm();
                    setSaveError("");
                  }}
                  disabled={form.isSubmitting}
                >
                  Discard changes
                </button>
              ) : (
                <button type="button" className="btn btn-light" onClick={toggle}>
                  Close
                </button>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={form.isSubmitting || !form.dirty}
              >
                {form.isSubmitting ? <Spinner size="sm" className="me-2" /> : null}
                Save changes
              </button>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ProfileSheetEditor;
